'use client';

import { useEffect, useState } from 'react';

export type WavespeedPriceQuote = { price?: number; base?: number; discountRate?: number };
type Quote = WavespeedPriceQuote & { key: string; error: boolean; at: number };
function priceDetails(data: WavespeedPriceQuote): WavespeedPriceQuote {
  return {
    price: data.price,
    base: typeof data.base === 'number' && Number.isFinite(data.base) && data.base >= 0 ? data.base : undefined,
    discountRate: typeof data.discountRate === 'number' && Number.isFinite(data.discountRate) &&
      data.discountRate >= 0 && data.discountRate <= 100 ? data.discountRate : undefined,
  };
}
const baseCache = new Map<string, Quote>();
let activeBaseRequests = 0;
const baseQueue: Array<() => void> = [];
async function baseSlot() {
  if (activeBaseRequests >= 4) await new Promise<void>((resolve) => baseQueue.push(resolve));
  else activeBaseRequests++;
  return () => {
    const next = baseQueue.shift();
    if (next) next();
    else activeBaseRequests--;
  };
}

/** Prices expire after one minute; a late response cannot price another input set. */
export function useWavespeedQuote(modelId: string, params?: Record<string, any>, enabled = true) {
  const key = JSON.stringify({ model: modelId, ...(params === undefined ? {} : { params }) });
  const baseOnly = params === undefined;
  const [state, setState] = useState<Quote>({ key: '', error: false, at: 0 });
  useEffect(() => {
    if (!enabled || !modelId) return;
    let disposed = false;
    let controller: AbortController | undefined;
    const refresh = async () => {
      if (document.visibilityState === 'hidden') return;
      const cached = baseOnly ? baseCache.get(key) : undefined;
      if (cached && Date.now() - cached.at < 60_000) { setState(cached); return; }
      controller?.abort();
      const current = new AbortController();
      controller = current;
      setState({ key, error: false, at: 0 });
      const release = baseOnly ? await baseSlot() : () => {};
      const timeout = setTimeout(() => current.abort(), 25_000);
      try {
        if (disposed || current.signal.aborted) return;
        const response = await fetch('/api/ai/wavespeed/quote', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: key, signal: current.signal, cache: 'no-store',
        });
        const json = await response.json();
        if (!response.ok || !json.success || typeof json.data?.price !== 'number' ||
            !Number.isFinite(json.data.price) || json.data.price < 0) throw new Error('Quote unavailable');
        const next = { key, ...priceDetails(json.data), at: Date.now(), error: false };
        if (disposed || controller !== current) return;
        if (baseOnly) {
          if (baseCache.size >= 1000) baseCache.delete(baseCache.keys().next().value!);
          baseCache.set(key, next);
        }
        setState(next);
      } catch {
        if (!disposed && controller === current) setState({ key, error: true, at: Date.now() });
      } finally {
        clearTimeout(timeout);
        release();
      }
    };
    const timer = setTimeout(refresh, baseOnly ? 0 : 300);
    const interval = setInterval(refresh, 60_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      disposed = true;
      controller?.abort();
      clearTimeout(timer);
      clearInterval(interval);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [key, modelId, baseOnly, enabled]);
  const current = enabled && state.key === key && Date.now() - state.at < 60_000 ? state : { key, error: false, at: 0 };
  return { ...current, updatePrice: (data: WavespeedPriceQuote) => {
    if (typeof data.price === 'number' && Number.isFinite(data.price) && data.price >= 0) {
      setState({ key, ...priceDetails(data), error: false, at: Date.now() });
    }
  } };
}
